import React from 'react';
import { keyBy } from 'lodash';
import { CampusResourcesQuery, EditOrganizationHandbookInput, Permission } from '../../graphQL';
import { AccommodationsSection } from './Section/AccommodationsSection';
import { BeaconResourcesSection } from './Section/BeaconResourcesSection';
import { CampusResourcesSection } from './Section/CampusResourcesSection';
import { CareAndCollaborationSection } from './Section/CareAndCollaborationSection';
import { ContinuityOfCareSection } from './Section/ContinuityOfCareSection';
import { EmergencyProtocolSection } from './Section/EmergencyProtocolSection';
import { MedicalSection } from './Section/MedicalSection';
import {
  titleAccommodations,
  titleBeaconResources,
  titleCampusResources,
  titleCareAndCollaboration,
  titleContinuityOfCare,
  titleEmergencyProtocol,
  titleMedical,
} from './Section/sectionConstants';
import { EditableSectionRowInfo, HandBookInfo } from './Section/types';

export enum HandbookPanelKey {
  careAndCollaboration = 'careAndCollaboration',
  campusResources = 'campusResources',
  accomodations = 'accomodations',
  continuityOfCare = 'continuityOfCare',
  medical = 'medical',
  emergencyProtocol = 'emergencyProtocol',
  beaconResources = 'beaconResources',
}

type ResourceList = CampusResourcesQuery['organization']['resources'];

export type HandbookPanelRenderContext = {
  organizationId: number;
  editable: boolean;
  verticalDisplay?: boolean;
  handbookInfo: HandBookInfo;
  campusResources: ResourceList;
  beaconResources: ResourceList;
  refetchResources: () => Promise<unknown>;
  selectedField: keyof HandBookInfo | null;
  onStartEdit?: (item: keyof HandBookInfo) => void;
  onCancelEdit?: () => void;
  onSave?: (editFieldName: keyof EditOrganizationHandbookInput, value: string) => Promise<void>;
};

type PanelDefinition = {
  key: HandbookPanelKey;
  title: string;
  editPermission: Permission;
  editableSectionRowInfo: EditableSectionRowInfo[];
  render: (ctx: HandbookPanelRenderContext) => JSX.Element;
};

const careAndCollaborationRows: EditableSectionRowInfo[] = [
  {
    label: 'Session Limits Renew Each Academic Year?',
    editInputKey: 'sessionLimitsRenew',
    displayKey: 'sessionLimitsRenew',
  },
  {
    label: 'No Shows and Late Cancellations count toward session limit?',
    info: 'Do patient no-shows and late cancellations (<24hr before appt) result in a deduction of a session?',
    editInputKey: 'noShowsCountForSessionLimits',
    displayKey: 'noShowsCountForSessionLimits',
  },
  {
    label: 'Default Therapy Cadence',
    editInputKey: 'defaultTherapyCadence',
    displayKey: 'defaultTherapyCadence',
  },
  {
    label: 'Co-pay charged by partner for Mantra services?',
    info: 'Does the partner charge a co-pay for Mantra services?',
    editInputKey: 'coPayChargedByPartner',
    displayKey: 'coPayChargedByPartner',
  },
];

const accommodationsRows: EditableSectionRowInfo[] = [
  {
    label: 'Requesting academic accommodations',
    editInputKey: 'requestingAcademicAccomodations',
    displayKey: 'requestingAcademicAccomodations',
  },
  {
    label: 'Full ADHD evaluation',
    editInputKey: 'fullADHDEvaluation',
    displayKey: 'fullADHDEvaluation',
  },
  {
    label: 'Protocol for obtaining Medical Leave documentation',
    editInputKey: 'protocolForMedicalLeaveDoc',
    displayKey: 'protocolForMedicalLeaveDoc',
  },
];

const continuityOfCareRows: EditableSectionRowInfo[] = [
  {
    label: 'Summer Sponsored Break: Who is eligible?',
    editInputKey: 'summerBreakEligibility',
    displayKey: 'summerBreakEligibility',
  },
  {
    label: 'Winter Sponsored Break: Who is eligible?',
    editInputKey: 'winterBreakEligibility',
    displayKey: 'winterBreakEligibility',
  },
];

const medicalRows: EditableSectionRowInfo[] = [
  {
    label: 'Labs',
    editInputKey: 'labsNearCampus',
    displayKey: 'labsNearCampus',
  },
  {
    label: 'EKGs',
    editInputKey: 'ekgsNearCampus',
    displayKey: 'ekgsNearCampus',
  },
  {
    label: 'Vitals',
    editInputKey: 'vitalsNearCampus',
    displayKey: 'vitalsNearCampus',
  },
];

const emergencyProtocolRows: EditableSectionRowInfo[] = [
  {
    label: 'High level protocol for managing emergencies',
    editInputKey: 'highLevelProtocolManagingEmergency',
    displayKey: 'highLevelProtocolManagingEmergency',
  },
  {
    label: 'Campus safety/police number',
    editInputKey: 'campusSafetyPoliceNumber',
    displayKey: 'campusSafetyPoliceNumber',
  },
  {
    label: 'Supporting Clinician',
    editInputKey: 'supportingClinician',
    displayKey: 'supportingClinician',
  },
  {
    label: 'Hospitalization',
    editInputKey: 'hospitalization',
    displayKey: 'hospitalization',
  },
  {
    label: 'Main point of contact to inform about the crisis',
    editInputKey: 'mainPointOfContactCrisis',
    displayKey: 'mainPointOfContactCrisis',
  },
  {
    label: 'On-Campus Crisis Team',
    editInputKey: 'campusCrisisTeamInProduct',
    displayKey: 'campusCrisisTeamInProduct',
  },
];

const HANDBOOK_PANELS: Readonly<PanelDefinition>[] = [
  {
    key: HandbookPanelKey.careAndCollaboration,
    editPermission: Permission.OrganizationHandbookEdit,
    title: titleCareAndCollaboration,
    editableSectionRowInfo: careAndCollaborationRows,
    render: ctx => (
      <CareAndCollaborationSection
        organizationId={ctx.organizationId}
        editable={ctx.editable}
        verticalDisplay={ctx.verticalDisplay}
        handbookInfo={ctx.handbookInfo}
        selectedField={ctx.selectedField}
        editableSectionRowInfo={careAndCollaborationRows}
        onStartEdit={ctx.onStartEdit}
        onCancelEdit={ctx.onCancelEdit}
        onSave={ctx.onSave}
      />
    ),
  },
  {
    key: HandbookPanelKey.campusResources,
    editPermission: Permission.OrganizationCampusResourcesEdit,
    title: titleCampusResources,
    editableSectionRowInfo: [],
    render: ctx => (
      <CampusResourcesSection
        organizationId={ctx.organizationId}
        editable={ctx.editable}
        verticalDisplay={ctx.verticalDisplay}
        resources={ctx.campusResources}
        refetch={ctx.refetchResources}
      />
    ),
  },
  {
    key: HandbookPanelKey.accomodations,
    editPermission: Permission.MantraAdmin,
    title: titleAccommodations,
    editableSectionRowInfo: accommodationsRows,
    render: ctx => (
      <AccommodationsSection
        editable={ctx.editable}
        verticalDisplay={ctx.verticalDisplay}
        handbookInfo={ctx.handbookInfo}
        selectedField={ctx.selectedField}
        editableSectionRowInfo={accommodationsRows}
        onStartEdit={ctx.onStartEdit}
        onCancelEdit={ctx.onCancelEdit}
        onSave={ctx.onSave}
      />
    ),
  },
  {
    key: HandbookPanelKey.continuityOfCare,
    editPermission: Permission.OrganizationHandbookEdit,
    title: titleContinuityOfCare,
    editableSectionRowInfo: continuityOfCareRows,
    render: ctx => (
      <ContinuityOfCareSection
        editable={ctx.editable}
        verticalDisplay={ctx.verticalDisplay}
        handbookInfo={ctx.handbookInfo}
        selectedField={ctx.selectedField}
        editableSectionRowInfo={continuityOfCareRows}
        onStartEdit={ctx.onStartEdit}
        onCancelEdit={ctx.onCancelEdit}
        onSave={ctx.onSave}
      />
    ),
  },
  {
    key: HandbookPanelKey.medical,
    editPermission: Permission.OrganizationHandbookEdit,
    title: titleMedical,
    editableSectionRowInfo: medicalRows,
    render: ctx => (
      <MedicalSection
        editable={ctx.editable}
        verticalDisplay={ctx.verticalDisplay}
        handbookInfo={ctx.handbookInfo}
        selectedField={ctx.selectedField}
        editableSectionRowInfo={medicalRows}
        onStartEdit={ctx.onStartEdit}
        onCancelEdit={ctx.onCancelEdit}
        onSave={ctx.onSave}
      />
    ),
  },
  {
    key: HandbookPanelKey.emergencyProtocol,
    editPermission: Permission.OrganizationHandbookEdit,
    title: titleEmergencyProtocol,
    editableSectionRowInfo: emergencyProtocolRows,
    render: ctx => (
      <EmergencyProtocolSection
        editable={ctx.editable}
        verticalDisplay={ctx.verticalDisplay}
        handbookInfo={ctx.handbookInfo}
        selectedField={ctx.selectedField}
        editableSectionRowInfo={emergencyProtocolRows}
        onStartEdit={ctx.onStartEdit}
        onCancelEdit={ctx.onCancelEdit}
        onSave={ctx.onSave}
      />
    ),
  },
  {
    key: HandbookPanelKey.beaconResources,
    editPermission: Permission.OrganizationSaiResourcesLibraryEdit,
    title: titleBeaconResources,
    editableSectionRowInfo: [],
    render: ctx => (
      <BeaconResourcesSection
        organizationId={ctx.organizationId}
        editable={ctx.editable}
        resources={ctx.beaconResources}
        refetch={ctx.refetchResources}
      />
    ),
  },
];

export const panelLookup = keyBy(HANDBOOK_PANELS, panel => panel.key);

export const getHandbookPanels = ({
  excludePanels,
}: { excludePanels?: HandbookPanelKey[] } = {}) => {
  return excludePanels
    ? HANDBOOK_PANELS.filter(panel => !excludePanels.includes(panel.key))
    : HANDBOOK_PANELS;
};
